/**
 * Platform-wide identity validation: Morfi handle + display name.
 *
 * The same constants and predicates are mirrored at
 * `backend/backend-api-service/src/utils/identity-validation.ts`. Both files
 * MUST stay in sync — the rules are the contract between frontend, backend,
 * and the database CHECK constraints on `users.handle` / `users.display_name`.
 *
 * If you change a rule here, update:
 *   1. The backend mirror.
 *   2. The DB CHECK constraint (apply migration).
 *   3. Any error-message i18n keys that quote the limits.
 */

export const HANDLE_RULES = {
  MIN: 4,
  MAX: 15,
  REGEX: /^[a-z0-9_]+$/,
} as const;

export const DISPLAY_NAME_RULES = {
  MAX: 50,
} as const;

export const RESERVED_HANDLES: ReadonlySet<string> = new Set([
  'admin',
  'morfi',
  'support',
  'help',
  'system',
  'mod',
  'moderator',
  'null',
  'undefined',
  'api',
  'www',
  'app',
  'settings',
  'profile',
  'onboarding',
]);

export type HandleValidationError =
  | 'too_short'
  | 'too_long'
  | 'bad_chars'
  | 'underscore_edge'
  | 'reserved';

export type HandleValidationResult = { ok: true } | { ok: false; error: HandleValidationError };

/**
 * Normalize a raw handle string: NFKC + strip leading `@` + lowercase + trim.
 * Always run before `validateHandle`.
 */
export function normalizeHandle(raw: string): string {
  return raw.normalize('NFKC').trim().replace(/^@/, '').toLowerCase();
}

/** Validate a *normalized* handle. */
export function validateHandle(handle: string): HandleValidationResult {
  if (handle.length < HANDLE_RULES.MIN) return { ok: false, error: 'too_short' };
  if (handle.length > HANDLE_RULES.MAX) return { ok: false, error: 'too_long' };
  if (!HANDLE_RULES.REGEX.test(handle)) return { ok: false, error: 'bad_chars' };
  if (handle.startsWith('_') || handle.endsWith('_') || handle.includes('__')) {
    return { ok: false, error: 'underscore_edge' };
  }
  if (RESERVED_HANDLES.has(handle)) return { ok: false, error: 'reserved' };
  return { ok: true };
}

export type DisplayNameValidationError = 'empty' | 'too_long';

export type DisplayNameValidationResult =
  | { ok: true; trimmed: string }
  | { ok: false; error: DisplayNameValidationError };

/**
 * Validate a display name. Trims first. Rejects whitespace-only and >50 chars.
 * HTML stripping is the server's responsibility (XSS belongs at the boundary).
 */
export function validateDisplayName(input: string): DisplayNameValidationResult {
  const trimmed = input.trim();
  if (trimmed.length === 0) return { ok: false, error: 'empty' };
  if (trimmed.length > DISPLAY_NAME_RULES.MAX) return { ok: false, error: 'too_long' };
  return { ok: true, trimmed };
}
